import posthog from 'posthog-js';

// Initialise PostHog once at app boot. We deliberately gate on the env var
// being present rather than on `import.meta.env.PROD` so that:
//   - production / preview / staging builds with the key set will track
//   - local dev runs without the key set (or pointed at a different
//     PostHog "Dev" project via Doppler) won't pollute the production
//     analytics with developer activity
//
// `person_profiles: 'identified_only'` keeps us under PostHog's profile
// limit by only creating Person records once we explicitly identify the
// user post-login. Anonymous visitors still send events (just without a
// stored person profile attached).

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
const POSTHOG_HOST =
  (import.meta.env.VITE_POSTHOG_HOST as string | undefined) || 'https://pulse.ingesta.ai';

let initialised = false;

export function initPostHog(): void {
  if (initialised) return;
  if (!POSTHOG_KEY) return;
  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    ui_host: 'https://us.posthog.com',
    person_profiles: 'identified_only',
    capture_pageview: true,
    autocapture: true,
  });
  initialised = true;
}

// `identify` accepts the email as the distinct id (per the team's choice)
// plus a properties bag for everything else we want pinned to the person.
// Idempotent — calling repeatedly with the same email is fine.
export function identifyUser(email: string, props?: Record<string, unknown>): void {
  if (!initialised) return;
  posthog.identify(email, props);
}

// Reset on logout so the next anonymous visitor isn't tagged as the
// previous user. PostHog's own docs make this the canonical pattern.
export function resetPostHog(): void {
  if (!initialised) return;
  posthog.reset();
}

// Thin typed wrapper for explicit business events. Auto-capture handles
// pageviews and clicks; this is for things we want to count semantically
// (e.g. "project_created", "executive_summary_regenerated").
export function track(event: string, props?: Record<string, unknown>): void {
  if (!initialised) return;
  posthog.capture(event, props);
}

export { posthog };
